import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { GET_CASH_FLOW, GET_TRANSACTIONS } from "graphql/transaction";
import TransactionsType from "enums/TransactionsType";
import TransactionStatus from "enums/TransactionStatus";
import { TimeFrameEnum } from "enums/AnalyticsTimeFrame";

/** One point on the balance curve. */
export interface CashFlowPoint {
  date: string;
  balance: number;
}

export interface CashFlowSummary {
  /** The balance curve, oldest point first. */
  series: CashFlowPoint[];
  currency: string;
  /** Money that arrived over the window. */
  inflow: number;
  /** Money that left over the window, as a positive number. */
  outflow: number;
  loading: boolean;
}

/**
 * Every type that moves money in or out of a company account. Card
 * authorizations are excluded: they are holds, and their capture is already
 * counted.
 */
const MOVEMENT_TYPES = [
  TransactionsType.CAPTURE,
  TransactionsType.INBOUND_PAYMENT,
  TransactionsType.INBOUND_TRANSFER,
  TransactionsType.INBOUND_WIRE,
  TransactionsType.INBOUND_FUNDS,
  TransactionsType.OUTBOUND_PAYMENT,
  TransactionsType.OUTBOUND_TRANSFER,
  TransactionsType.OUTBOUND_WIRE,
  TransactionsType.REFUND,
  TransactionsType.REFUND_PAYMENT,
];

const SETTLED_STATUSES = [
  TransactionStatus.COMPLETE,
  TransactionStatus.PENDING,
  TransactionStatus.REFUNDED,
  TransactionStatus.PARTIALLY_REFUNDED,
  TransactionStatus.REFUND_SUCCESS,
  TransactionStatus.RETURNED,
  TransactionStatus.REVERSED,
];

/**
 * `getCashFlow` walks backwards from today's balance and clips the result at
 * zero, so its deltas cannot answer "how much moved". The in/out pair sums the
 * ledger instead, over at most this many rows: the query has no aggregate and
 * one page is cheaper than paging a month of spend to add it up.
 */
const MOVEMENT_SAMPLE = 500;

interface CashFlowQuery {
  getCashFlow?: {
    currency?: string | null;
    cashFlowData?: CashFlowPoint[] | null;
  } | null;
}

interface MovementQuery {
  transactions?: {
    edges?: { node: { id: string; amount?: number | null } }[] | null;
  } | null;
}

/**
 * The balance curve plus the money in and out behind it, for the balance panel.
 * A positive `amount` is money arriving, which is the sign convention
 * `_computeCashFlow` walks the ledger with on the server.
 */
const useCashFlowSummary = (
  timeFrame: TimeFrameEnum = TimeFrameEnum.LAST_30_DAYS,
  accountId?: string | null,
  /**
   * Hold both queries. For a caller whose account is not resolved yet: a null
   * accountId means every account, so asking early draws the wrong curve.
   */
  skip = false,
  /**
   * Skip the movement sample. The in/out totals need their own 500-row
   * GET_TRANSACTIONS pass, which is pure cost for a caller that only draws the
   * curve.
   */
  skipMovements = false,
): CashFlowSummary => {
  const { data: flow, loading: flowLoading } = useQuery<CashFlowQuery>(
    GET_CASH_FLOW,
    {
      variables: {
        type: MOVEMENT_TYPES,
        transactionStatus: SETTLED_STATUSES,
        date: timeFrame,
        accountId: accountId ?? null,
      },
      fetchPolicy: "cache-and-network",
      skip,
    },
  );

  const { data: movement, loading: movementLoading } = useQuery<MovementQuery>(
    GET_TRANSACTIONS,
    {
      variables: {
        first: MOVEMENT_SAMPLE,
        type: MOVEMENT_TYPES,
        transactionStatus: SETTLED_STATUSES,
        date: timeFrame,
        ...(accountId ? { accountId } : {}),
      },
      fetchPolicy: "cache-and-network",
      skip: skip || skipMovements,
    },
  );

  const { inflow, outflow } = useMemo(() => {
    let credited = 0;
    let debited = 0;

    (movement?.transactions?.edges ?? []).forEach(({ node }) => {
      const amount = node.amount ?? 0;
      if (amount >= 0) credited += amount;
      else debited += Math.abs(amount);
    });

    return { inflow: credited, outflow: debited };
  }, [movement]);

  return {
    series: flow?.getCashFlow?.cashFlowData ?? [],
    currency: flow?.getCashFlow?.currency ?? "usd",
    inflow,
    outflow,
    loading: flowLoading || movementLoading,
  };
};

export default useCashFlowSummary;
